import React, { FC, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { PublishStatus, IncentiveEnrolmentRecord, IncentiveItem } from '../../../types/incentive-types';
import { useDispatch } from 'react-redux';
import { publishIncentive, unpublishIncentive, fetchIncentiveEnrolmentRecords } from '../../../network/incentiveCrud';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import AsyncCsvLink from '../../../../../common/components/AsyncCsvLink';
import { getDefaultDisplayDate } from '../../../../../common/utils/time-utils';
import { PaginateList } from '../../../../../common/types/common-types';
import { useIntl } from 'react-intl';
import PruTable from '../../../../../common/components/PruTable/PruTable';
import { useHistory } from 'react-router-dom';
import { INCENTIVE_BASE_PATH } from '../../../constants';
import UploadResultDialog from './UploadResultDialog';

type IncentiveListProps = {
  isLoading: boolean;
  incentiveList?: PaginateList<IncentiveItem>;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
};

const useStyles = makeStyles()({
  operationBtn: {
    color: 'blue',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
  },
});

type DialogState = {
  isOpen: boolean;
  code: string;
  resultFileName: string;
};

const IncentiveList: FC<IncentiveListProps> = ({ isLoading, incentiveList, onRefresh, onChangePage }) => {
  const history = useHistory();
  const { classes } = useStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    code: '',
    resultFileName: '',
  });

  const csvDataGenerator = (data: IncentiveEnrolmentRecord[]) => {
    let csvData = [
      [
        'Agent Code',
        'Incentive Code',
        'Incentive Name',
        'Registration Required',
        'Start Date',
        'End Date',
        'Registration Status',
        'Enrolment Date',
      ],
    ];
    data.forEach((row) => {
      csvData.push([
        row.agentCode,
        row.code,
        row.name,
        row.registrationRequired,
        row.startDate,
        row.endDate,
        row.registrationStatus,
        row.enrolmentDate,
      ]);
    });
    return csvData;
  };

  const onPublish = async (code: string) => {
    try {
      await publishIncentive(code, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Published successfully - ${code}`,
          },
        ]),
      );
      onRefresh();
    } catch (err) {}
  };

  const onUnpublish = async (code: string) => {
    try {
      await unpublishIncentive(code, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Unpublished successfully - ${code}`,
          },
        ]),
      );
      onRefresh();
    } catch (err) {}
  };

  return (
    <>
      <UploadResultDialog
        key={`upload-csv-dialog-${dialogState.isOpen}`}
        code={dialogState.code}
        resultFileName={dialogState.resultFileName}
        open={dialogState.isOpen}
        onClose={() => setDialogState({ isOpen: false, code: '', resultFileName: '' })}
        onRefresh={onRefresh}
      />
      <PruTable
        title={Translation('incentive.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('incentive.newIncentive'),
            onClick: () => history.push(`${INCENTIVE_BASE_PATH}/create`),
          },
        ]}
        disableBulkSelect
        operationDef={[
          {
            title: Translation('section.common.operation.edit'),
            tooltipText: 'Edit Incentive',
            onClick: (row) => history.push(`${INCENTIVE_BASE_PATH}/edit/${row.code}`),
            condition: (row) => row.publishStatus === PublishStatus.DRAFT,
          },
          {
            title: Translation('section.common.operation.view'),
            tooltipText: 'View Incentive',
            onClick: (row) => history.push(`${INCENTIVE_BASE_PATH}/edit/${row.code}`),
            condition: (row) => row.publishStatus !== PublishStatus.DRAFT,
          },
          {
            title: Translation('section.common.operation.publish'),
            tooltipText: 'Publish Incentive',
            onClick: (row) => onPublish(row.code),
            condition: (row) => row.publishStatus !== PublishStatus.PUBLISHED,
          },
          {
            title: Translation('section.common.operation.unpublish'),
            tooltipText: 'Unpublish Incentive',
            onClick: (row) => onUnpublish(row.code),
            condition: (row) => row.publishStatus === PublishStatus.PUBLISHED,
          },
          {
            title: Translation('section.common.operation.upload'),
            tooltipText: 'Upload CSV Result',
            onClick: (row) => setDialogState({ isOpen: true, code: row.code, resultFileName: row.resultFileName }),
          },
        ]}
        columnDef={[
          {
            isId: true,
            keyIndex: 'code',
            displayName: Translation('incentive.common.code'),
            renderData: (row) => row.code,
          },
          {
            keyIndex: 'name',
            displayName: Translation('incentive.common.name'),
            renderData: (row) => row.name,
          },
          {
            keyIndex: 'startDate',
            align: 'center',
            displayName: Translation('incentive.common.startDate'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.startDate)),
          },
          {
            keyIndex: 'endDate',
            align: 'center',
            displayName: Translation('incentive.common.endDate'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.endDate)),
          },
          {
            keyIndex: 'publishStatus',
            align: 'center',
            displayName: Translation('incentive.common.status'),
            renderData: (row) => row.publishStatus,
          },
          {
            keyIndex: 'numOfEnrolledAgent',
            align: 'center',
            displayName: Translation('incentive.common.noOfAgentsRegistered'),
            renderData: (row) => (
              <AsyncCsvLink
                isDisabled={row.numOfEnrolledAgent <= 0}
                filename={`${row.code}.csv`}
                dataParser={csvDataGenerator}
                asyncCall={() => fetchIncentiveEnrolmentRecords(row.code, dispatch)}
              >
                {row.numOfEnrolledAgent > 0 ? (
                  <div className={classes.operationBtn}>{row.numOfEnrolledAgent}</div>
                ) : (
                  <>{row.numOfEnrolledAgent}</>
                )}
              </AsyncCsvLink>
            ),
          },
          {
            keyIndex: 'createdDate',
            align: 'center',
            displayName: Translation('incentive.common.createdDate'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.createdDate)),
          },
        ]}
        isLoading={isLoading}
        onRefresh={onRefresh}
        dataSource={incentiveList?.items}
        totalPages={incentiveList?.totalPages}
        totalRecords={incentiveList?.total}
        onChangePage={onChangePage}
      />
    </>
  );
};

export default IncentiveList;
